import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Toast } from '@/components/Toast';
import {
  ABC_LINK_PARAM,
  abcLinkOpeningText,
  openAbcLink,
  type AbcLinkNotice,
} from '@/data/abcLink';

/** Long enough to read; an error stays a little longer. */
const NOTICE_MS = { info: 4000, error: 7000 } as const;

type ShownNotice = AbcLinkNotice & { pending?: boolean };

/**
 * Reads an ABC import link (`?abc=<address>`, data/abcLink.ts) on any page.
 * App mounts it only while the music is open, so a link that arrives at the
 * sign-in screen keeps its address in the URL (which sign-in returns to) and
 * is read once the reader is in. While it is read the workbench holds its own
 * address-bar handling (useTuneUrl), and the link is replaced in the history
 * by where it led.
 */
export function AbcLink() {
  const location = useLocation();
  const navigate = useNavigate();
  const [notice, setNotice] = useState<ShownNotice | null>(null);
  const address = new URLSearchParams(location.search).get(ABC_LINK_PARAM);

  useEffect(() => {
    if (address === null) return;
    const page = { protocol: window.location.protocol };
    const controller = new AbortController();
    let live = true;
    setNotice({ text: abcLinkOpeningText(address, page), tone: 'info', pending: true });
    void openAbcLink(
      { pathname: location.pathname, search: location.search, ...page },
      { signal: controller.signal },
    ).then((outcome) => {
      if (!live) return;
      navigate(outcome.to, { replace: true });
      setNotice(outcome.status === 'stale' ? null : (outcome.notice ?? null));
    });
    return () => {
      live = false;
      controller.abort();
      setNotice((shown) => (shown?.pending ? null : shown));
    };
    // Keyed on the address alone: the place it arrived at is read once, and a
    // re-render that keeps the link (or a new `navigate` identity) must not
    // fetch it again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  useEffect(() => {
    if (!notice || notice.pending) return;
    const timer = window.setTimeout(() => setNotice(null), NOTICE_MS[notice.tone]);
    return () => window.clearTimeout(timer);
  }, [notice]);

  return notice ? <Toast testId="abc-link-notice">{notice.text}</Toast> : null;
}
